import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import "./index.css";

import AuthGate from "@/components/AuthGate";
import SubscriptionGate from "@/components/SubscriptionGate";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SWRProvider } from "@/lib/swr-config";
import { PodcastProvider } from "@/contexts/PodcastContext";
import EpisodesPage from "@/pages/EpisodesPage";
import ChatPage from "@/pages/ChatPage";
import ProfilePage from "@/pages/ProfilePage";
import BillingPage from "@/pages/BillingPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AddPodcastPage from "@/pages/AddPodcastPage";
import PricingPage from "@/pages/PricingPage";
import PaywallPage from "@/pages/PaywallPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import PaymentMethodSuccessPage from "@/pages/PaymentMethodSuccessPage";
import PaymentMethodFailurePage from "@/pages/PaymentMethodFailurePage";
import FAQPage from "@/pages/FAQPage";
import TryEpisodePage from "@/pages/TryEpisodePage";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <AuthGate>
      <SubscriptionGate>
        <PodcastProvider>{children}</PodcastProvider>
      </SubscriptionGate>
    </AuthGate>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/magic" element={<LoginPage />} />

      {/* Paywall & Billing - AuthGated but NOT SubscriptionGated */}
      <Route
        path="/paywall"
        element={
          <AuthGate>
            <PaywallPage />
          </AuthGate>
        }
      />
      <Route
        path="/billing/success"
        element={
          <AuthGate>
            <BillingSuccessPage />
          </AuthGate>
        }
      />
      <Route
        path="/billing/payment-method/success"
        element={
          <AuthGate>
            <PaymentMethodSuccessPage />
          </AuthGate>
        }
      />
      <Route
        path="/billing/payment-method/failure"
        element={
          <AuthGate>
            <PaymentMethodFailurePage />
          </AuthGate>
        }
      />
      <Route
        path="/billing"
        element={
          <AuthGate>
            <PodcastProvider>
              <BillingPage />
            </PodcastProvider>
          </AuthGate>
        }
      />

      {/* Protected App Routes - AuthGated AND SubscriptionGated */}
      <Route
        path="/episodes"
        element={
          <ProtectedRoute>
            <EpisodesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/podcasts/add"
        element={
          <ProtectedRoute>
            <AddPodcastPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/try"
        element={
          <ProtectedRoute>
            <TryEpisodePage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SWRProvider>
            <AppRoutes />
          </SWRProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
